import React, { FunctionComponent, useState, MouseEvent } from 'react';
import { Box, Paper, makeStyles, Typography } from '@material-ui/core';
import Modal from '../../../../components/Modal';
import DeleteDialog from '../../../../components/DeleteDialog';
import FileUploadDialog from '../../../../components/FileUploadDialog';
import { File, FileList } from '../../../../store/modules/application/types';
import { doubleEncodeURL } from '../../../../utils/utils';
import Loader from '../../../../components/Loader';
import Button from '@material-ui/core/Button';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import InfiniteScrollTableView from '../../../../components/InfiniteScrollTableView';

type FilesProps = {
    skip: number;
    data: FileList;
    citationId: string;
    loading: boolean;
    hasMore: boolean;
    setSkip: Function;
    getFileList: Function;
    isUploadModalOpen: boolean;
    handleUploadFile: Function;
    deleteFileDocument: Function;
    onCloseUploadModal: Function;
    downloadFileDocument: Function;
    getAuthenticationToken: Function;
};
interface IColumn {
    id: 'fileName|string' | 'uploadedDate|date' | 'size|size' | 'metadata.documenttype|string';
    label: string;
    maxWidth?: number;
    align?: 'right' | 'left' | 'center';
    format?: (value: number) => string;
    field: string;
    noSort?: boolean;
}
const columns: IColumn[] = [
    { id: 'fileName|string', label: 'Name', maxWidth: 300, field: 'fileName', noSort: true },
    { id: 'uploadedDate|date', label: 'Date Uploaded', maxWidth: 200, field: 'uploadedDate', noSort: true },
    {
        id: 'size|size',
        label: 'Size',
        align: 'left',
        maxWidth: 200,
        field: 'size',
        noSort: true,
    },
    {
        id: 'metadata.documenttype|string',
        label: 'Type',
        align: 'left',
        maxWidth: 200,
        field: 'documenttype',
        noSort: true,
    },
];

const useStyles = makeStyles({
    searchIconContainer: {
        width: '100%',
        maxHeight: 300,
        color: 'gray',
        paddingTop: '40px',
        paddingBottom: '150px',
    },
    noResultFound: {
        fontSize: '24px',
        color: '#484848',
        lineHeight: '24px',
        fontWeight: 600,
        marginTop: '3rem',
        textAlign: 'center',
    },
    noResultFoundMessage2: {
        fontSize: '18px',
        color: '#484848',
        lineHeight: '24px',
        marginTop: '20px',
        fontWeight: 500,
        textAlign: 'center',
    },
    noResultFoundFileType: {
        fontSize: '16px',
        color: '#6E7788',
        lineHeight: '24px',
        fontWeight: 500,
        textAlign: 'center',
    },
    noResultFoundUploadFile: {
        fontSize: '18px',
        color: '#0277BD',
        lineHeight: '24px',
        fontWeight: 500,
        textAlign: 'center',
        marginTop: '20px',
        cursor: 'pointer',
    },
    uploadButton: {
        color: '#0277BD',
        marginRight: '40px',
        fontWeight: 600,
        cursor: 'pointer',
        letterSpacing: '0.3px,',
        display: 'flex',
        alignItems: 'center',
        marginBottom: '25px',
        justifyContent: 'flex-end',
    },
    uploadButtonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
});

const Files: FunctionComponent<FilesProps> = (props) => {
    const [fileId, setFileId] = useState<string>('');

    /* State for modals */
    const [isDeleteModalOpen, setModal] = useState(false);
    const {
        data,
        skip,
        hasMore,
        loading,
        setSkip,
        citationId,
        getFileList,
        downloadFileDocument,
        deleteFileDocument,
        isUploadModalOpen,
        onCloseUploadModal,
        getAuthenticationToken,
        handleUploadFile,
    } = props;

    const classes = useStyles();
    /* Handle delete case button */
    const handleDeleteCase = (id: string, e: any) => {
        setFileId(id);
        setModal(true);
        e.stopPropagation();
    };

    const onClose = () => {
        setModal(false);
        onCloseUploadModal(false);
    };
    const onDelete = () => {
        setModal(false);
        deleteFileDocument(citationId, doubleEncodeURL(fileId));
    };
    const onClickRow = (id: number, e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
    };
    const loadMore = () => {
        if (hasMore) {
            setSkip(skip + 1);
        }
    };
    const handleDownloadCase = (file: File, e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        downloadFileDocument(citationId, doubleEncodeURL(file?.id), file?.fileName);
    };
    if (loading) {
        return <Loader />;
    }
    return (
        <div className="verticalFillContainer">
            <div className={classes.uploadButtonContainer}>
                <Button
                    startIcon={<CloudUploadIcon />}
                    className={classes.uploadButton}
                    onClick={(e: MouseEvent<HTMLElement>) => {
                        handleUploadFile && handleUploadFile(e);
                    }}
                >
                    UPLOAD FILE
                </Button>
            </div>
            <Paper className="case-entries-container verticalFillContent">
                {data?.length > 0 ? (
                    <InfiniteScrollTableView
                        columns={columns}
                        data={data}
                        count={data.length}
                        deleteFlag={true}
                        downloadFlag={true}
                        hasMore={hasMore}
                        loadMore={loadMore}
                        onClickRow={onClickRow}
                        handleDeleteCase={handleDeleteCase}
                        handleDownloadCase={handleDownloadCase}
                    />
                ) : (
                    <Box className={classes.searchIconContainer} display="flex" flexDirection="column">
                        <Box className={classes.noResultFound} display="flex" justifyContent="center">
                            No files yet
                        </Box>
                        <Box className={classes.noResultFoundMessage2} display="flex" justifyContent="center">
                            All your files will be stored here
                        </Box>
                        <Box className={classes.noResultFoundFileType} display="flex" justifyContent="center">
                            Allowed file types: doc, jpg, png, pdf, xls, csv
                        </Box>
                        <Box display="flex" justifyContent="center">
                            <Typography
                                className={classes.noResultFoundUploadFile}
                                onClick={(e: MouseEvent<HTMLElement>) => handleUploadFile(e)}
                            >
                                Upload File
                            </Typography>
                        </Box>
                    </Box>
                )}

                {(isDeleteModalOpen || isUploadModalOpen) && (
                    <Modal open={true} maxWidth={'md'} fullWidth={true} minHeight={'10vh'}>
                        {isDeleteModalOpen && (
                            <DeleteDialog
                                onClose={onClose}
                                onDelete={onDelete}
                                contentTitle="Are you sure you'd like to proceed with this action?"
                                content=""
                            />
                        )}
                        {isUploadModalOpen && (
                            <FileUploadDialog
                                onClose={onClose}
                                citationId={citationId}
                                getAuthenticationToken={getAuthenticationToken}
                                getFileList={getFileList}
                            />
                        )}
                    </Modal>
                )}
            </Paper>
        </div>
    );
};

export default Files;
